import { useState, type FormEvent } from "react";
import { InlineSpinner } from "../components/InlineSpinner";
import { ApiError, apiRequest, getMe } from "../lib/api";
import { InlineError } from "../components/StudioShell";
import { navigate } from "../lib/navigation";
import { chooseResumeStep } from "../lib/resumeStep";
import type { SessionKind, WorkStep } from "@studio/shared";
import { pathForStep, saveSession, updateSession } from "../lib/session";

interface JoinContext {
  code: string;
  title: string;
  humming_enabled: boolean;
  kind: SessionKind;
}

interface JoinResponse {
  student_token: string;
  work_id: string;
  kind: SessionKind;
}

function readContext(code: string): JoinContext {
  try {
    const parsed = JSON.parse(sessionStorage.getItem("mvs:join-context") ?? "null") as JoinContext | null;
    if (parsed?.code === code) return parsed;
  } catch {
    // The server validates the code again on join.
  }
  return {
    code,
    title: "음악 만들기",
    humming_enabled: false,
    kind: "music",
  };
}

/**
 * 다시 들어온 학생은 저장된 단계로 보낸다.
 *
 * 🚨 Codex 검수 2026-10-13 P0-3: 서버는 같은 이름의 학생에게 기존 작품을 돌려주는데, 종전엔 늘 첫 단계로 보냈다.
 *    곡 횟수를 다 쓴 학생이 다른 기기로 들어오면 만들기 버튼이 막힌 화면에 갇혀 자기 곡으로 갈 길이 없었다.
 *    진입 화면 자동 이어하기(router.tsx)와 같은 규칙 — `/api/me` 의 step 과 진행 중 잡을 따른다.
 *    단계를 못 읽거나 그 수업에 없는 단계면 첫 단계로 간다(새 학생과 같다).
 */
async function resumeStep(workId: string, kind: SessionKind): Promise<WorkStep> {
  try {
    const me = await getMe();
    updateSession({ kind: me.session_kind, activeJobId: me.active_job?.id ?? null }, workId);
    return chooseResumeStep(me, workId, kind);
  } catch {
    // 입장은 이미 끝났다. 첫 단계에서 시작해도 작품은 서버에 그대로 있다.
    return chooseResumeStep(null, workId, kind);
  }
}

export function NamePage() {
  const code = new URLSearchParams(window.location.search).get("code") ?? "";
  const context = readContext(code);
  const [name, setName] = useState(() => localStorage.getItem("mvs:last-name") ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const normalizedName = name.trim();
    if (normalizedName.length < 2) {
      setError("이름을 2자 이상 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await apiRequest<JoinResponse>("/api/join", {
        method: "POST",
        authenticated: false,
        body: JSON.stringify({ code, name: normalizedName }),
      });
      localStorage.setItem("mvs:last-name", normalizedName);
      saveSession({
        token: result.student_token,
        workId: result.work_id,
        name: normalizedName,
        code,
        hummingEnabled: context.humming_enabled,
        kind: result.kind,
      });
      setName(normalizedName);
      setComplete(true);
      const step = await resumeStep(result.work_id, result.kind);
      window.setTimeout(() => navigate(pathForStep(result.work_id, step), { replace: true }), 1_200);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "수업에 참여하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  if (complete) {
    return (
      <main className="join-page">
        <section className="join-card join-complete" aria-live="polite">
          <div className="success-icon" aria-hidden="true">✓</div>
          <h1 tabIndex={-1}>{name}님, 시작해볼까요!</h1>
          <p>잠시 후 이동합니다</p>
        </section>
      </main>
    );
  }

  return (
    <main className="join-page">
      <section className="join-card">
        <div className="person-icon" aria-hidden="true">♩</div>
        <h1 tabIndex={-1}>이름을 알려주세요</h1>
        <p className="join-session-summary">{context.title} · {code}</p>
        <form onSubmit={(event) => void join(event)}>
          <label className="sr-only" htmlFor="student-name">이름</label>
          <input
            id="student-name"
            className="name-input"
            placeholder="이름을 입력하세요"
            maxLength={30}
            autoComplete="name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-invalid={Boolean(error)}
            disabled={submitting}
          />
          <p className="join-hint compact">선생님이 누가 만들었는지 알 수 있어요</p>
          {error ? <InlineError>{error}</InlineError> : null}
          <button className="button-primary join-primary" type="submit" disabled={submitting || name.trim().length < 2}>
            {submitting ? <InlineSpinner label="참여 중..." /> : "수업 참여하기"}
          </button>
        </form>
        <button className="button-ghost" type="button" onClick={() => navigate(`/join?code=${encodeURIComponent(code)}`)}>
          돌아가기
        </button>
      </section>
    </main>
  );
}
